import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef } from 'react';

import type { ColorType } from '@lib/types';
import { cn, cva, type VariantProps, setThisClass } from '@lib/utils';

import { SpinnerInnerDotIcon } from './SpinnerInnerDotIcon';

const spinnerVariants = cva(
  [
    // Layout
    'inline-flex rounded-full',
  ],
  {
    variants: {
      variant: {
        default: 'border-this-solid',
        soft: 'border-this-bg',
        innerDot: 'text-this-solid',
      },
      size: {
        xs: 'size-4 border-2',
        sm: 'size-5 border-2',
        md: 'size-7 border-3',
        lg: 'size-10 border-4',
        xl: 'size-12 border-4',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

type SpinnerProps = {
  color?: ColorType;
  animate?: boolean;
  elastic?: boolean;
  disabled?: boolean;
} & VariantProps<typeof spinnerVariants> &
  ComponentPropsWithoutRef<'div'>;

const Spinner = forwardRef<HTMLDivElement, SpinnerProps>(
  (
    {
      className,
      variant = 'default',
      size = 'md',
      animate = true,
      elastic = false,
      disabled = false,
      color = 'neutral',
      style,
      ...rest
    },
    ref
  ) => {
    const animationClasses = cn(
      animate && !disabled && 'animate-spin',
      elastic && '[animation-timing-function:var(--ease-elastic)]',
      disabled && 'opacity-50'
    );

    if (variant === 'innerDot') {
      return (
        <div
          ref={ref}
          className={cn(
            // Base styles without border (handled by SVG)
            'inline-flex',
            // Size (without border)
            size === 'xs' && 'size-4',
            size === 'sm' && 'size-5',
            size === 'md' && 'size-7',
            size === 'lg' && 'size-10',
            size === 'xl' && 'size-12',
            // Animation & state
            animationClasses,
            // Color
            'text-this-solid',
            setThisClass(color),
            className
          )}
          style={style}
          aria-disabled={disabled || undefined}
          {...rest}
        >
          <SpinnerInnerDotIcon className="h-full w-full" />
        </div>
      );
    }

    // Inline style for the "gap" border to avoid CSS layer conflicts
    const borderStyle =
      variant === 'default'
        ? { borderRightColor: 'transparent', ...style }
        : { borderRightColor: 'var(--this-solid)', ...style };

    return (
      <div
        ref={ref}
        className={cn(
          spinnerVariants({ variant, size }),
          animationClasses,
          setThisClass(color),
          className
        )}
        style={borderStyle}
        aria-disabled={disabled || undefined}
        {...rest}
      />
    );
  }
);

Spinner.displayName = 'Spinner';

export { Spinner };
export type { SpinnerProps };
